import {noop} from '@benchling/js-utils';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {ReactFlowProvider} from 'reactflow';

import {ProcessSingletonTaskSchemaName} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import FlowchartSidepanelManager from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelManager';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import ProcessFlowchartConfigEditorContent from '../ProcessFlowchartConfigEditorContent';
import {FlowchartNode} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigMetadata} from '../redux/reduceFlowchartConfig';
import {SingletonTaskSchemasContext} from '../SingletonTaskSchemasContext';
import TransitionsManager from '../types/transition/TransitionsManager';
import {ProcessTaskDefinitionsResult} from '../useAllSchemasById';
import {
  FlowchartConfigWithPartialMetadata,
  LiveStateChanges,
} from '../useFlowchartState/BaseFlowchartConfigStateManager';
import {validateFlowchartData} from '../useFlowchartValidationAndAutosaverControl';
import {LoadedFlowchartData} from './executionFlowchartData';
import {ExecutionEditorMode} from './ExecutionFlowchartPaneTypes';

const NOOP_LIVE_STATE_CHANGES: LiveStateChanges = {
  dirtyState: [false, noop],
  readonlyState: [false, noop],
  isSaveInFlightState: [false, noop],
  isAutosaverPausedState: [false, noop],
  isAutosaverBlockedState: [false, noop],
};

interface ExecutionFlowchartEditorProps {
  editorMode: ExecutionEditorMode;
  data: LoadedFlowchartData;
  executionEditorConfigurator: ProcessFlowchartEditorConfigurator;
  draftState: FlowchartConfigWithMetadata;
  setDraftState: (draftState: FlowchartConfigWithMetadata) => void;
  onBlockingErrorsChange?: (hasBlockingErrors: boolean) => void;
  allSchemasById: SchemaById;
  taskDefinitionsResult: ProcessTaskDefinitionsResult;
  singletonTaskSchemasByName: Record<
    ProcessSingletonTaskSchemaName,
    ProcessTaskSchemaWithoutFlowchartConfigGraphQL
  > | null;
}

function applyPartialUpdate(
  current: FlowchartConfigWithMetadata,
  partial: FlowchartConfigWithPartialMetadata
): FlowchartConfigWithMetadata {
  const updatedStaticFlowchart = partial.staticFlowchart ?? current.staticFlowchart;
  let updatedMetadata = current.metadata;
  if (partial.flowchartConfigMetadata != null) {
    const definedEntries = Object.entries(partial.flowchartConfigMetadata).filter(
      ([, value]) => value !== undefined
    );
    updatedMetadata = {
      ...current.metadata,
      ...Object.fromEntries(definedEntries),
    };
  }
  return {staticFlowchart: updatedStaticFlowchart, metadata: updatedMetadata};
}

export default function ExecutionFlowchartEditor(props: ExecutionFlowchartEditorProps): JSX.Element {
  const {
    editorMode,
    data,
    executionEditorConfigurator,
    draftState: draftFlowchartConfigAndMetadata,
    setDraftState,
    onBlockingErrorsChange,
    allSchemasById,
    taskDefinitionsResult,
    singletonTaskSchemasByName: loadedSingletonSchemas,
  } = props;

  const singletonTaskSchemasByName = useMemo(
    () =>
      loadedSingletonSchemas ??
      ({} as Record<ProcessSingletonTaskSchemaName, ProcessTaskSchemaWithoutFlowchartConfigGraphQL>),
    [loadedSingletonSchemas]
  );

  const taskDefinitionsInFlowchart = useMemo(() => {
    if (taskDefinitionsResult == null) {
      return null;
    }
    const flowchartTaskSchemaIds = new Set([
      data.rootTaskSchema.id,
      ...data.availableTaskSchemas.map((schema) => schema.id),
    ]);
    return taskDefinitionsResult.filter((def) =>
      flowchartTaskSchemaIds.has(def.latestVersion.processTaskSchema.id)
    );
  }, [taskDefinitionsResult, data.rootTaskSchema.id, data.availableTaskSchemas]);

  const getNodeByIdRef = useRef<(nodeId: string) => FlowchartNode | null>(() => null);
  const getNodeNameById = useCallback(
    (nodeId: string): string | null => getNodeByIdRef.current(nodeId)?.data.nodeName ?? null,
    []
  );

  const internalDraftStateRef = useRef(draftFlowchartConfigAndMetadata);
  internalDraftStateRef.current = draftFlowchartConfigAndMetadata;

  const setDraftFlowchartConfigAndMetadata = useCallback(
    (
      updater: (current: FlowchartConfigWithMetadata) => FlowchartConfigWithPartialMetadata | null,
      _saveImmediately?: boolean,
      _skipSettingDirtyState?: boolean
    ): Promise<FlowchartConfigMetadata | null> => {
      const partial = updater(internalDraftStateRef.current);
      if (partial == null) {
        return Promise.resolve(null);
      }
      const next = applyPartialUpdate(draftFlowchartConfigAndMetadata, partial);
      setDraftState(next);
      internalDraftStateRef.current = next;
      return Promise.resolve(next.metadata);
    },
    [draftFlowchartConfigAndMetadata, setDraftState]
  );

  const plugin = PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(
    executionEditorConfigurator.flowchartTypeName
  );

  const validationErrors: Array<ProcessFlowchartValidationError> = useMemo(
    () =>
      validateFlowchartData(
        draftFlowchartConfigAndMetadata.staticFlowchart,
        data.flowchartConfigId,
        draftFlowchartConfigAndMetadata.metadata,
        data.rootTaskSchema,
        false,
        plugin,
        undefined,
        editorMode
      ),
    [draftFlowchartConfigAndMetadata, data.flowchartConfigId, data.rootTaskSchema, plugin, editorMode]
  );

  const hasBlockingErrors = useMemo(
    () => validationErrors.some((error) => error.blocksPersistence),
    [validationErrors]
  );

  useEffect(() => {
    onBlockingErrorsChange?.(hasBlockingErrors);
  }, [hasBlockingErrors, onBlockingErrorsChange]);

  return (
    <ReactFlowProvider>
      <SingletonTaskSchemasContext.Provider value={singletonTaskSchemasByName}>
        <ReactFlowLockedContext.Provider value={false}>
          <FlowchartSidepanelManager
            getNodeNameById={getNodeNameById}
            flowchartType={executionEditorConfigurator.flowchartTypeName}
          >
            <TransitionsManager draftFlowchartConfigAndMetadata={draftFlowchartConfigAndMetadata}>
              <ProcessFlowchartConfigEditorContent
                getNodeByIdRef={getNodeByIdRef}
                flowchart={draftFlowchartConfigAndMetadata.staticFlowchart}
                flowchartEditorConfigurator={executionEditorConfigurator}
                editorMode={editorMode}
                allSchemasById={allSchemasById}
                rootTaskSchema={data.rootTaskSchema}
                flowchartTaskDefinitionId={data.flowchartTaskDefinitionId}
                flowchartConfigId={data.flowchartConfigId}
                taskDefinitionsResult={taskDefinitionsInFlowchart}
                didInitialConfigHaveConditionalMapping={false}
                didInitialFlowchartHaveChainedRouters={data.hasChainedRouters}
                liveStateChanges={NOOP_LIVE_STATE_CHANGES}
                locked={false}
                validationErrors={validationErrors}
                draftFlowchartConfigAndMetadata={draftFlowchartConfigAndMetadata}
                setDraftFlowchartConfigAndMetadata={setDraftFlowchartConfigAndMetadata}
                initiallyExpandAddNodeTab={editorMode !== FlowchartEditorMode.EXECUTION_VIEWER}
              />
            </TransitionsManager>
          </FlowchartSidepanelManager>
        </ReactFlowLockedContext.Provider>
      </SingletonTaskSchemasContext.Provider>
    </ReactFlowProvider>
  );
}
